<template>
	<BaseLayout :include-padding="false" :content-full-height="true" background-color-class="bg-color-white">
		<template v-if="isIframe" #header>
			<HeaderBasic classes="bg-color-white">
				<span class="display-none-smo bl ml-100 pl-100">
					<p class="mt-0">{{ formattedOfferingName }}</p>
				</span>
			</HeaderBasic>
		</template>

		<BasePlaceHolder
			:placehold="loading"
			:section-count="5"
			:class="['py-250 py-400-md', { 'container position-relative': loading }]"
		>
			<div>
				<FundDetailsHero v-if="fund" class="mb-200 mb-400-md" :fund="fund">
					<template #unauthenticated-button>
						<BaseLink
							data-test="sign-in-button"
							:link="{
								router: 'login',
								text: 'Sign in',
								target: isIframe ? '_blank' : ''
							}"
							class="button button-block button-primary mt-100"
						/>
					</template>
				</FundDetailsHero>
			</div>

			<VentureCapitalCaseStudy v-if="caseStudyData" :case-study="caseStudyData" :full-width="false" />

			<JumpLinkNav class="pt-150 pt-250-md" :jump-links="jumpLinks" :root-margin="500" :sticky-header="!isIframe">
				<div id="fund-strategy" class="container py-200 py-400-md" data-context="fund-strategy">
					<h2 class="heading-4 mb-100">Fund strategy</h2>
					<div class="display-flex-smo flex-direction-column-smo gap-100-smo grid-md gutters-300-md mt-100">
						<div class="col-7-md">
							<RichTextComponent
								v-if="fundStrategy"
								class="body-lg-md article-body"
								:blocks="fundStrategy"
							/>
						</div>
						<div class="col-5-md mt-150-smo order-3-smo">
							<div v-if="showIndustryTypeAllocations" class="display-flex flex-direction-column gap-300">
								<AllocationBreakdownTable
									table-heading="Industry"
									:allocations="formattedIndustryTypeAllocations"
									:set-border="false"
								/>
							</div>
						</div>
						<div class="col-7-md order-2-smo">
							<div
								:class="[
									'investment-detail__container mt-100 mt-200-md py-200-md',
									{ bt: !appStore.isMobile }
								]"
							>
								<div class="display-flex flex-direction-column-smo gap-150-md">
									<div v-if="operatingExpensesTable" class="operating-expenses-table mr-150-md">
										<p
											v-if="operatingExpensesTableTitle"
											class="mb-75 text-color-content-secondary"
											>{{ operatingExpensesTableTitle }}</p
										>
										<div
											v-for="(data, idx) in operatingExpensesTable.tableData"
											:key="idx"
											:class="[
												'ml-150 mb-25 display-flex justify-content-space-between',
												{
													'operating-expenses-table-divider mb-50 pb-50': data.displayDivider
												}
											]"
										>
											<span class="text-color-content-secondary">{{ data.name }}</span>
											<span
												class="font-weight-bold pl-50"
												data-test="operating-expenses-table-value"
												>{{ formatPercent(data.value) }}</span
											>
										</div>
									</div>
									<div class="display-flex flex-direction-column-smo flex-wrap-wrap-md gap-150-md">
										<div
											v-for="detail in investmentDetails"
											:key="detail.key"
											class="investment-detail-item"
										>
											<div class="text-color-content-secondary body-md">
												{{ detail.key }}
											</div>
											<div class="body-md font-weight-bold">
												{{ detail.value }}
											</div>
										</div>
									</div>
								</div>
								<RichTextComponent
									v-if="operatingExpensesTable && operatingExpensesTableDisclaimer"
									:blocks="operatingExpensesTableDisclaimer"
									class="disclaimer mt-100 mt-200-md mb-200-smo"
								/>
							</div>
						</div>
					</div>
				</div>

				<div
					v-if="showSimulatedReturns"
					id="fund-simulated-returns"
					data-context="fund-simulated-returns"
					data-test="simulated-return"
					class="container py-200 py-400-md"
				>
					<h2 class="heading-4">Hypothetical growth of $10,000</h2>
					<SimulatedReturns
						v-if="fund"
						:fund="fund"
						:show-net-return-chart="showNetReturnChart"
						:show-value-chart="showValueChart"
					/>
				</div>

				<div
					id="fund-historical-net-returns"
					class="container py-200 py-400-md overflow-hidden"
					data-context="fund-historical-net-returns"
				>
					<h2 class="heading-4 mb-100 mb-200-md">Historical net return</h2>
					<div v-if="showHistoricalReturns" data-test="returns-table">
						<HistoricalReturnsTable :yearly-returns="historicalPerformanceData" />
					</div>
					<p
						v-if="showNetReturnChart || showHistoricalReturns"
						class="body-sm muted text-align-center"
						data-test="disclaimer"
					>
						<BaseLinkButton class="muted text-decoration-underline" @click="openDisclaimerModal"
							>Learn more</BaseLinkButton
						>
						about the assumptions in this section, or view our
						<BaseLink class="muted text-decoration-underline" :link="disclosuresLink"
							>full{{ '\xa0' }}disclosure</BaseLink
						>.
					</p>
					<div
						v-if="hasNoChartsDisplayed"
						data-test="performance-data-placeholder"
						class="col-9-md col-8-lg body-lg-md"
					>
						Performance data will become available once the {{ offeringName }} begins reporting a net asset
						value per share (NAV). To learn more, please see the offering circular or contact us at
						<BaseLink :link="investmentsEmailLink" />.
					</div>
				</div>

				<div
					v-if="assetElements.length || !isMergedFund"
					id="fund-assets"
					class="py-200 py-400-md container"
					data-context="fund-assets"
				>
					<div v-if="assetElements.length" data-test="assets-section">
						<AssetElementContainer
							:asset-elements="assetElements"
							:filter-options="filterOptions"
							:use-icon-view-options="true"
							:show-disclaimer-if-applicable="false"
						/>
					</div>
					<div v-else-if="!isMergedFund" data-test="no-assets-available">
						<h2 class="heading-4 mb-150">Assets</h2>
						<div class="col-9-md col-8-lg body-lg-md">
							The {{ offeringName }} recently began ramping up and has not yet acquired its first project.
							As with our other offerings, investors can expect timely reporting on any new acquisitions,
							progress, or payoffs.
						</div>
					</div>
				</div>

				<div
					v-if="showLiterature"
					id="fund-literature"
					class="py-200 py-400-md"
					data-test="literature-section"
					data-context="fund-literature"
				>
					<div class="container">
						<h2 class="heading-4 mb-50 mb-150">Literature</h2>
						<LiteratureSection
							:literature-list="literatureItems"
							:disclaimer="literatureDisclaimer"
							disclaimer-classes="body-lg-md"
						/>
					</div>
				</div>
			</JumpLinkNav>
		</BasePlaceHolder>
		<template v-if="footerDisclaimer" #footer>
			<BasePlaceHolder :line-count="3" :placehold="loading" :class="{ container: loading }">
				<FooterOfferingDetails :disclaimer="footerDisclaimer" :fund-name="offeringName" />
			</BasePlaceHolder>
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { disclosuresLink, investmentsEmailLink } from '@constants/links';
import { Fund, FundLiterature, FundPerformanceForYear, OperatingExpensesTable } from 'types/funds';
import { useRoute, useRouter } from 'vue-router';
import AllocationBreakdownTable from '@components/offering-details/allocation-breakdown-table.vue';
import { Asset } from 'types/assets';
import AssetElementContainer from '@components/asset-element/asset-element-container.vue';
import { AssetFilterOptions } from 'types/asset-details';
import { disclaimerModalContent } from '@constants/reit';
import DisclaimerSimple from '@components/disclaimer/disclaimer-simple.vue';
import FooterOfferingDetails from '@components/footer/footer-offering-details.vue';
import FundDetailsHero from '@components/offering-details/fund-details/fund-details-hero.vue';
import { getAssets } from '@sanity/proxy/assets';
import { getFund } from '@sanity/proxy/funds';
import { getVentureMarketingPageCaseStudy } from '@api/sanity/graphql/venture-marketing-page';
import { growthTechFooter } from '@constants/offering-details';
import HeaderBasic from '@components/header/header-basic.vue';
import HistoricalReturnsTable from '@components/offering-details/historical-returns-table.vue';
import { JumpLink } from 'types/layout';
import JumpLinkNav from '@components/layout/jump-link-nav.vue';
import LiteratureSection from '@components/offering-details/literature-section.vue';
import { percentage } from '@filters/shared-filters';
import { PercentageAllocation } from 'types/reit-details';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityMarketingCaseStudy } from 'types/sanity/marketing-components';
import { SanityRichTextBlock } from 'types/sanity';
import SimulatedReturns from './simulated-returns.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import VentureCapitalCaseStudy from '@components/marketing-site/venture-capital/venture-case-study.vue';

interface Props {
	isIframe?: boolean;
}

withDefaults(defineProps<Props>(), {
	isIframe: false
});

const route = useRoute();
const router = useRouter();

const appStore = useAppStore();

const fund = ref<Fund | null>(null);

const historicalPerformanceData = ref<Array<FundPerformanceForYear>>([]);

const assetElements = ref<Array<Asset>>([]);
const offeringId = ref('');
const offeringName = ref('');
const literatureItems = ref<Array<FundLiterature>>([]);
const literatureDisclaimer = ref<Array<SanityRichTextBlock>>([]);

const loading = ref(true);

const industry = ref<Array<string>>([]);
const assetType = ref<Array<string>>([]);

const caseStudyData: Ref<SanityMarketingCaseStudy | null> = ref(null);

const footerDisclaimer = ref<Array<SanityRichTextBlock> | null>(null);

const filterOptions = computed(
	(): Array<AssetFilterOptions> => [
		{
			filterClass: 'industry',
			filterClassLabel: 'Industry',
			filterType: industry.value
		},
		{
			filterClass: 'assetType',
			filterClassLabel: 'Asset type',
			filterType: assetType.value
		}
	]
);

const fundStrategy = computed((): Array<SanityRichTextBlock> | null => fund.value?.fundOverview ?? null);

const showIndustryTypeAllocations = computed((): boolean => formattedIndustryTypeAllocations.value.length > 0);

const showValueChart = computed(
	(): boolean => !!fund.value?.simulatedInvestmentReturns && fund.value?.simulatedInvestmentReturns.length > 0
);

const showNetReturnChart = computed(
	(): boolean =>
		!!fund.value?.simulatedReturnsByTimePeriod?.quarterlyReturnDetails &&
		fund.value?.simulatedReturnsByTimePeriod.quarterlyReturnDetails.length > 0
);

const showSimulatedReturns = computed((): boolean => showNetReturnChart.value || showValueChart.value);

const showHistoricalReturns = computed((): boolean => historicalPerformanceData.value.length > 0);

const hasNoChartsDisplayed = computed((): boolean => !showHistoricalReturns.value && !showSimulatedReturns.value);

const showLiterature = computed((): boolean => literatureItems.value?.length > 0);

const operatingExpensesTable = computed(
	(): OperatingExpensesTable | null => fund.value?.fundDetails?.operatingExpensesTable ?? null
);

const operatingExpensesTableTitle = computed((): string => operatingExpensesTable.value?.title ?? '');
const operatingExpensesTableDisclaimer = computed(
	(): Array<SanityRichTextBlock> | null => operatingExpensesTable.value?.disclaimer ?? null
);

const formattedIndustryTypeAllocations = computed((): Array<PercentageAllocation> => {
	return (fund.value?.industryAllocations ?? []).map((allocation) => {
		return {
			percentage: allocation.allocationPercent,
			allocation: allocation.name
		};
	});
});

const jumpLinks = computed((): Array<JumpLink> => {
	const jumpLinksToReturn: Array<JumpLink> = [{ key: 'fund-strategy', label: 'Fund strategy' }];

	if (showSimulatedReturns.value) {
		jumpLinksToReturn.push({
			key: 'fund-simulated-returns',
			label: 'Hypothetical growth'
		});
	}

	if (showHistoricalReturns.value) {
		jumpLinksToReturn.push({
			key: 'fund-historical-net-returns',
			label: 'Historical return'
		});
	}

	if (assetElements.value.length) {
		jumpLinksToReturn.push({ key: 'fund-assets', label: 'Assets' });
	}

	if (showLiterature.value) {
		jumpLinksToReturn.push({
			key: 'fund-literature',
			label: 'Literature'
		});
	}

	return jumpLinksToReturn;
});

const investmentDetails = computed((): Array<{ key: string; value: string }> => {
	return [
		{
			key: 'Distribution frequency',
			value: fund.value?.distribution ?? ''
		},
		{
			key: 'Tax reporting',
			value: fund.value?.taxForms ?? ''
		},
		{
			key: 'Management fee',
			value: fund.value?.managementFee ? `${fund.value?.managementFee}%` : ''
		},
		{
			key: 'Total annual operating expenses',
			value: fund.value?.totalAnnualOperatingExpense
				? percentage(+fund.value.totalAnnualOperatingExpense / 100, true, 2, '', false)
				: ''
		}
	].filter((item) => !!item.value);
});

const formattedOfferingName = computed((): string => `Fundrise ${offeringName.value}`);

const isMergedFund = computed((): boolean => fund.value?.phase?.name === 'MERGED');

function formatPercent(val: string): string {
	return Number(val).toFixed(2) + '%';
}

function openDisclaimerModal(): void {
	appStore.updateCurrentModal({
		modal: DisclaimerSimple,
		props: {
			content: disclaimerModalContent,
			dataContext: 'Reit Details charts'
		}
	});
}

function initializeFilterOptions(): void {
	assetElements.value.forEach((element) => {
		if (
			element.ventureCapitalFields?.industry &&
			!industry.value.includes(element.ventureCapitalFields.industry.name)
		) {
			industry.value.push(element.ventureCapitalFields.industry.name);
		}
		if (!assetType.value.includes(element.assetType.name)) {
			assetType.value.push(element.assetType.name);
		}
	});
}

async function setup() {
	try {
		offeringId.value = route.params['offeringId'] as string;

		const [fundResponse, assetsResponse, ventureSanityCaseStudy] = await Promise.all([
			await getFund(offeringId.value, {
				include: ['ANNUALIZED_RETURNS', 'HISTORICAL_RETURNS', 'SIMULATED_RETURNS', 'FUND_DETAILS']
			}),
			await getAssets({ fundId: [offeringId.value], status: 'ACTIVE' }),
			await getVentureMarketingPageCaseStudy()
		]);

		caseStudyData.value = ventureSanityCaseStudy;

		fund.value = fundResponse;

		if (!fund.value) {
			await router.push({ name: 'offerings' });
			return;
		}

		assetElements.value = assetsResponse.results;
		historicalPerformanceData.value = fund.value.historicalPerformanceReturns ?? [];
		offeringName.value = fund.value.name ?? '';
		literatureItems.value = fund.value.literature ?? [];
		literatureDisclaimer.value = fund.value.literatureDisclaimer ?? [];
		footerDisclaimer.value = fund.value.fundDetails?.footerDisclaimer?.length
			? fund.value.fundDetails.footerDisclaimer
			: growthTechFooter;

		initializeFilterOptions();

		useHead({
			title: offeringName.value,
			meta: [
				{
					name: 'description',
					content: `Learn more about the goal, strategy, assets, performance, and time horizon of the ${offeringName.value}.`
				},
				{
					property: 'og:description',
					content: `Learn more about the goal, strategy, assets, performance, and time horizon of the ${offeringName.value}.`
				}
			]
		});

		loading.value = false;
	} catch (e) {
		await router.push({ name: 'offerings' });
	}
}

setup();
</script>

<style lang="scss" scoped>
@use '../../styles/utilities/respond-to.scss' as *;
@use '../../styles/constants/_colors.scss' as *;

.operating-expenses-table {
	min-width: 400px;

	@include respond-to(smo) {
		order: 1;
		border-top: 1px solid token('bg-tertiary');
		padding-top: 1rem;
		min-width: 0;
	}

	&-divider {
		border-bottom: 1px solid token('content-primary');
	}
}

.investment-detail__container {
	@include respond-to(md) {
		width: fit-content;
		min-width: 620px;
	}
	.investment-detail-item {
		display: flex;
		@include respond-to(smo) {
			justify-content: space-between;
			padding: 16px 0;
			gap: 8px;
		}

		@include respond-to(md) {
			flex-direction: column;
			flex-shrink: 0;
			min-width: 172px;
		}

		&:not(:last-of-type) {
			@include respond-to(smo) {
				border-bottom: 1px solid token('bg-tertiary');
			}
		}
	}
}
</style>
